import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import PhoneCell from "../components/PhoneCell";
import ImportLeadsModal from "../components/ImportLeadsModal";
import { SOURCES, STATUSES, labelFor } from "../lib/constants";
import { useLeadOptions } from "../lib/useLeadOptions";
import { useAuth } from "../context/AuthContext";
import { Plus, MagnifyingGlass, DownloadSimple, Trash, PencilSimple, X, ArrowsClockwise, UploadSimple, TrashSimple, ChatText } from "@phosphor-icons/react";
import { toast } from "sonner";
import usePaginatedLeads from "../lib/usePaginatedLeads";

const blankLead = {
  customer_name: "",
  phone: "",
  source: "google_ads",
  status: "new",
  branch_id: "",
  agent_id: "",
  notes: "",
};

export default function Leads() {
  const { user, can } = useAuth();
  const isAdmin = user?.role === "admin";
  const { sources: allSources, statuses: allStatuses } = useLeadOptions();

  const [branches, setBranches] = useState([]);
  const [agents, setAgents] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [sideLoading, setSideLoading] = useState(true);

  const initialFilter = {
    source: "", status: "", branch_id: "", agent_id: "",
    date_from: "", date_to: "", search: "",
    email: "", phone: "",
    unassigned: false, unbranched: false,
    has_notes: "", has_comments: "",
    campaign_id: "",
    has_utm: "", has_gclid: "", has_fbclid: "",
  };
  const [filter, setFilter] = useState(initialFilter);

  const [editing, setEditing] = useState(null); // lead object being edited or "new"
  const [form, setForm] = useState(blankLead);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ agent_id: "", branch_id: "", status: "" });
  const [bulkRemarkOpen, setBulkRemarkOpen] = useState(false);
  const [bulkRemark, setBulkRemark] = useState("");
  const [importOpen, setImportOpen] = useState(false);

  const branchMap = useMemo(() => Object.fromEntries(branches.map((b) => [b.id, b.name])), [branches]);
  const agentMap = useMemo(() => Object.fromEntries(agents.map((a) => [a.id, a.name])), [agents]);

  const buildLeadParams = () => {
    const params = {};
    Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
    return params;
  };

  const {
    leads, setLeads, total, loading: leadsLoading, hasMore, isLoadingMore, loadMore,
    reload: reloadLeads,
  } = usePaginatedLeads(buildLeadParams, [JSON.stringify(filter)]);

  const loading = leadsLoading || sideLoading;

  const loadSide = async () => {
    setSideLoading(true);
    try {
      const [b, ag, cp] = await Promise.all([
        branches.length ? Promise.resolve({ data: branches }) : api.get("/branches"),
        agents.length ? Promise.resolve({ data: agents }) : api.get("/agents"),
        campaigns.length ? Promise.resolve({ data: campaigns }) : api.get("/campaigns"),
      ]);
      setBranches(b.data);
      setAgents(ag.data);
      setCampaigns(cp.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
    setSideLoading(false);
  };

  const load = async () => {
    await Promise.all([loadSide(), reloadLeads()]);
  };

  useEffect(() => { loadSide(); /* eslint-disable-next-line */ }, []);

  const openNew = () => {
    setForm({
      ...blankLead,
      branch_id: branches[0]?.id || "",
      agent_id: isAdmin ? (agents[0]?.id || "") : user.id,
    });
    setEditing("new");
  };

  const openEdit = (lead) => {
    setForm({
      customer_name: lead.customer_name,
      phone: lead.phone,
      source: lead.source,
      status: lead.status,
      branch_id: lead.branch_id,
      agent_id: lead.agent_id,
      notes: lead.notes || "",
      campaign_id: lead.campaign_id || "",
      revenue: lead.revenue || "",
    });
    setEditing(lead);
  };

  const close = () => { setEditing(null); setForm(blankLead); };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      // Empty campaign_id → send empty string to detach on server
      if (payload.campaign_id === "") payload.campaign_id = "";
      // Revenue is a number; treat empty string as unset
      if (payload.revenue === "" || payload.revenue === null || payload.revenue === undefined) {
        delete payload.revenue;
      } else {
        payload.revenue = parseFloat(payload.revenue) || 0;
      }
      if (editing === "new") {
        await api.post("/leads", payload);
        toast.success("Lead created");
      } else {
        await api.patch(`/leads/${editing.id}`, payload);
        toast.success("Lead updated");
      }
      close();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSaving(false);
  };

  const quickStatus = async (lead, status, force = false) => {
    try {
      const url = force ? `/leads/${lead.id}?force=true` : `/leads/${lead.id}`;
      await api.patch(url, { status });
      toast.success(`Status → ${labelFor(STATUSES, status)}${force ? " · admin override" : ""}`);
      load();
    } catch (e) {
      const msg = formatApiError(e);
      // Can't-close-early guard — admin can override
      if (msg.includes("Cannot close as") && isAdmin) {
        if (window.confirm(`${msg}\n\nOverride the cadence guard?`)) {
          return quickStatus(lead, status, true);
        }
      } else {
        toast.error(msg);
      }
    }
  };

  const remove = async (lead) => {
    if (!window.confirm(`Delete lead "${lead.customer_name}"?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const buildExportParams = (includeFilters) => {
    const params = new URLSearchParams();
    if (!includeFilters) return params;
    Object.entries(filter).forEach(([k, v]) => {
      if (v === "" || v === null || v === undefined || v === false) return;
      params.append(k, String(v));
    });
    return params;
  };

  const exportCsv = async (includeFilters = true) => {
    const params = buildExportParams(includeFilters);
    try {
      const res = await api.get(
        `/leads/export${params.toString() ? "?" + params.toString() : ""}`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      const tag = includeFilters && params.toString() ? "filtered" : "all";
      a.download = `leads_${tag}_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(includeFilters && params.toString() ? "Filtered CSV downloaded" : "Full CSV downloaded");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    setSelected((s) => {
      if (s.size === leads.length) return new Set();
      return new Set(leads.map((l) => l.id));
    });
  };
  const clearSelection = () => setSelected(new Set());

  const applyBulk = async (e) => {
    e.preventDefault();
    const payload = { lead_ids: Array.from(selected) };
    if (bulkForm.agent_id) payload.agent_id = bulkForm.agent_id;
    if (bulkForm.branch_id) payload.branch_id = bulkForm.branch_id;
    if (bulkForm.status) payload.status = bulkForm.status;
    if (Object.keys(payload).length === 1) {
      toast.error("Pick at least one field to change");
      return;
    }
    try {
      const { data } = await api.patch("/leads/bulk-reassign", payload);
      toast.success(`Updated ${data.updated} lead${data.updated !== 1 ? "s" : ""}`);
      setBulkOpen(false);
      setBulkForm({ agent_id: "", branch_id: "", status: "" });
      clearSelection();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const bulkDelete = async () => {
    const n = selected.size;
    if (!window.confirm(`Permanently delete ${n} selected lead${n !== 1 ? "s" : ""}? This cannot be undone.`)) return;
    try {
      const { data } = await api.post("/leads/bulk-delete", { lead_ids: Array.from(selected) });
      toast.success(`Deleted ${data.deleted} lead${data.deleted !== 1 ? "s" : ""}`);
      clearSelection();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const applyBulkRemark = async (e) => {
    e.preventDefault();
    const body = bulkRemark.trim();
    if (!body) { toast.error("Remark cannot be empty"); return; }
    try {
      const { data } = await api.post("/leads/bulk-comment", { lead_ids: Array.from(selected), body });
      toast.success(`Remark added to ${data.updated} lead${data.updated !== 1 ? "s" : ""}`);
      setBulkRemark("");
      setBulkRemarkOpen(false);
      clearSelection();
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const lastRemark = (l) => {
    const c = l.comments && l.comments.length ? l.comments[l.comments.length - 1] : null;
    return c ? c.body : "";
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="Pipeline"
        title="Leads"
        description="Update status, assign agents and track every enquiry from any source."
        actions={
          <>
            {can("can_import") && (
              <button
                onClick={() => setImportOpen(true)}
                data-testid="import-leads-button"
                className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
              >
                <UploadSimple size={16} /> Import CSV
              </button>
            )}
            {can("can_export") && (
              <>
                <button
                  onClick={() => exportCsv(true)}
                  data-testid="export-csv-filtered"
                  title="Export only the leads currently shown by the filters"
                  className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
                >
                  <DownloadSimple size={16} /> Export filtered
                </button>
                <button
                  onClick={() => exportCsv(false)}
                  data-testid="export-csv-all"
                  title="Export everything you have access to, ignoring filters"
                  className="px-4 py-2.5 text-sm font-semibold border border-border hover:bg-secondary rounded-sm flex items-center gap-2 transition-colors"
                >
                  <DownloadSimple size={16} /> Export all
                </button>
              </>
            )}
            <button
              onClick={openNew}
              data-testid="add-lead-button"
              className="px-4 py-2.5 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm flex items-center gap-2 transition-colors"
            >
              <Plus size={16} weight="bold" /> Add Lead
            </button>
          </>
        }
      />

      {/* Filters */}
      <div className="px-8 py-4 border-b border-border bg-white flex flex-wrap gap-2 items-center" data-testid="leads-filters">
        <div className="relative">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            placeholder="Search name / phone / email / notes"
            value={filter.search}
            onChange={(e) => setFilter({ ...filter, search: e.target.value })}
            data-testid="leads-search-input"
            className="text-sm border border-border pl-9 pr-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-72"
          />
        </div>
        <input
          placeholder="Phone contains"
          value={filter.phone}
          onChange={(e) => setFilter({ ...filter, phone: e.target.value })}
          data-testid="leads-filter-phone"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-40"
        />
        <input
          placeholder="Email contains"
          value={filter.email}
          onChange={(e) => setFilter({ ...filter, email: e.target.value })}
          data-testid="leads-filter-email"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none w-44"
        />
        <select value={filter.source} onChange={(e) => setFilter({ ...filter, source: e.target.value })}
          data-testid="leads-filter-source"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">All sources</option>
          {allSources.map((s) => <option key={s.id} value={s.id}>{s.label}{s.custom ? " ·" : ""}</option>)}
        </select>
        <select value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          data-testid="leads-filter-status"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">All statuses</option>
          {allStatuses.map((s) => <option key={s.id} value={s.id}>{s.label}{s.custom ? " ·" : ""}</option>)}
        </select>
        <select value={filter.branch_id} onChange={(e) => setFilter({ ...filter, branch_id: e.target.value })}
          data-testid="leads-filter-branch"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">All branches</option>
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        {isAdmin && (
          <select value={filter.agent_id} onChange={(e) => setFilter({ ...filter, agent_id: e.target.value })}
            data-testid="leads-filter-agent"
            className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
            <option value="">All agents</option>
            {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        )}
        <select value={filter.campaign_id} onChange={(e) => setFilter({ ...filter, campaign_id: e.target.value })}
          data-testid="leads-filter-campaign"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">All campaigns</option>
          {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.active === false ? " · paused" : ""}</option>)}
        </select>
        <select value={filter.has_notes} onChange={(e) => setFilter({ ...filter, has_notes: e.target.value })}
          data-testid="leads-filter-notes"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">Any notes</option>
          <option value="true">Has notes</option>
          <option value="false">No notes</option>
        </select>
        <select value={filter.has_comments} onChange={(e) => setFilter({ ...filter, has_comments: e.target.value })}
          data-testid="leads-filter-comments"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none">
          <option value="">Any remarks</option>
          <option value="true">Has remarks</option>
          <option value="false">No remarks</option>
        </select>
        <input type="date" value={filter.date_from} onChange={(e) => setFilter({ ...filter, date_from: e.target.value })}
          data-testid="leads-filter-from"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none" />
        <span className="text-muted-foreground text-xs">→</span>
        <input type="date" value={filter.date_to} onChange={(e) => setFilter({ ...filter, date_to: e.target.value })}
          data-testid="leads-filter-to"
          className="text-sm border border-border px-3 py-2 rounded-sm bg-white focus:border-primary focus:outline-none" />
        {isAdmin && (
          <>
            <button
              onClick={() => setFilter({ ...filter, unassigned: !filter.unassigned })}
              data-testid="leads-filter-unassigned"
              className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border rounded-sm ${
                filter.unassigned ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
              }`}
            >
              Unassigned
            </button>
            <button
              onClick={() => setFilter({ ...filter, unbranched: !filter.unbranched })}
              data-testid="leads-filter-unbranched"
              className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border rounded-sm ${
                filter.unbranched ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
              }`}
            >
              No branch
            </button>
          </>
        )}
        <button
          onClick={() => setFilter({ ...filter, has_utm: filter.has_utm === true ? "" : true })}
          data-testid="leads-filter-has-utm"
          title="Show only leads that carry any UTM tag"
          className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border rounded-sm ${
            filter.has_utm === true ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"
          }`}
        >
          Has UTM
        </button>
        <button
          onClick={() => setFilter({ ...filter, has_gclid: filter.has_gclid === true ? "" : true })}
          data-testid="leads-filter-has-gclid"
          title="Show only leads with a Google click ID (paid Google Ads)"
          className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border rounded-sm ${
            filter.has_gclid === true ? "bg-[#4285F4] text-white border-[#4285F4]" : "border-border hover:bg-secondary"
          }`}
        >
          gclid
        </button>
        <button
          onClick={() => setFilter({ ...filter, has_fbclid: filter.has_fbclid === true ? "" : true })}
          data-testid="leads-filter-has-fbclid"
          title="Show only leads with a Meta click ID (paid Meta Ads)"
          className={`text-xs uppercase tracking-[0.15em] font-bold px-3 py-2 border rounded-sm ${
            filter.has_fbclid === true ? "bg-[#1877F2] text-white border-[#1877F2]" : "border-border hover:bg-secondary"
          }`}
        >
          fbclid
        </button>
        {Object.values(filter).some((v) => v !== "" && v !== false) && (
          <button
            onClick={() => setFilter(initialFilter)}
            data-testid="leads-filter-clear"
            className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline"
          >
            Clear
          </button>
        )}
        <span className="ml-auto text-xs tabular-nums text-muted-foreground" data-testid="leads-count">
          {leads.length.toLocaleString()} of {total.toLocaleString()} record{total !== 1 && "s"}
        </span>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="px-8 py-3 bg-[#002FA7] text-white flex items-center gap-4 fade-up" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold">
            {selected.size} selected
          </span>
          {can("can_bulk_actions") && (
            <button onClick={() => setBulkOpen(true)} data-testid="bulk-reassign-button"
              className="ml-auto px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-white/40 hover:bg-white/10 flex items-center gap-2">
              <ArrowsClockwise size={14} /> Bulk reassign
            </button>
          )}
          {can("can_bulk_actions") && (
            <button onClick={() => setBulkRemarkOpen(true)} data-testid="bulk-remark-button"
              className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-white/40 hover:bg-white/10 flex items-center gap-2">
              <ChatText size={14} /> Add remark
            </button>
          )}
          {can("can_delete_leads") && can("can_bulk_actions") && (
            <button onClick={bulkDelete} data-testid="bulk-delete-button"
              className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold border border-white/40 bg-destructive/30 hover:bg-destructive flex items-center gap-2">
              <TrashSimple size={14} weight="fill" /> Delete
            </button>
          )}
          <button onClick={clearSelection} data-testid="bulk-clear"
            className="px-3 py-1.5 text-xs uppercase tracking-[0.15em] font-bold hover:bg-white/10">
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="px-8 py-6">
        <div className="border border-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                <th className="px-3 py-3 w-8">
                  <input
                    type="checkbox"
                    checked={leads.length > 0 && selected.size === leads.length}
                    onChange={toggleSelectAll}
                    data-testid="select-all-leads"
                    className="accent-primary"
                    aria-label="Select all"
                  />
                </th>
                <th className="px-4 py-3 font-bold">Customer</th>
                <th className="px-4 py-3 font-bold">Phone</th>
                <th className="px-4 py-3 font-bold">Source</th>
                <th className="px-4 py-3 font-bold">Branch</th>
                <th className="px-4 py-3 font-bold">Agent</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Last Remark</th>
                <th className="px-4 py-3 font-bold">Created</th>
                <th className="px-4 py-3 font-bold text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="row-stagger">
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-12 text-center text-sm text-muted-foreground">Loading…</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={10} className="px-4 py-16 text-center">
                  <div className="text-sm text-muted-foreground mb-3">No leads match these filters yet.</div>
                  <button onClick={openNew} data-testid="empty-add-lead" className="text-xs uppercase tracking-[0.15em] font-bold text-primary hover:underline">
                    + Add your first lead
                  </button>
                </td></tr>
              ) : leads.map((l) => (
                <tr key={l.id} className={`border-b border-border last:border-0 group ${selected.has(l.id) ? "bg-primary/5" : "hover:bg-secondary/40"}`} data-testid={`lead-row-${l.id}`}>
                  <td className="px-3 py-3">
                    <input
                      type="checkbox"
                      checked={selected.has(l.id)}
                      onChange={() => toggleSelect(l.id)}
                      data-testid={`select-lead-${l.id}`}
                      className="accent-primary"
                      aria-label={`Select ${l.customer_name}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">
                    <Link to={`/leads/${l.id}`} className="hover:text-primary hover:underline" data-testid={`lead-link-${l.id}`}>
                      {l.customer_name}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums">
                    <PhoneCell lead={l} onRevealed={(phone) => setLeads((prev) => prev.map((x) => x.id === l.id ? { ...x, phone, phone_masked: null } : x))} />
                  </td>
                  <td className="px-4 py-3">{labelFor(SOURCES, l.source)}</td>
                  <td className="px-4 py-3">{branchMap[l.branch_id] || "—"}</td>
                  <td className="px-4 py-3">{agentMap[l.agent_id] || "—"}</td>
                  <td className="px-4 py-3">
                    <select
                      value={l.status}
                      onChange={(e) => quickStatus(l, e.target.value)}
                      data-testid={`lead-status-select-${l.id}`}
                      className="text-xs border border-border px-2 py-1 rounded-sm bg-white font-semibold focus:border-primary focus:outline-none"
                      style={{ color: (allStatuses.find((s) => s.id === l.status) || STATUSES.find((s) => s.id === l.status))?.color }}
                    >
                      {/* Ensure current value (even if custom from CSV) is selectable */}
                      {!allStatuses.find((s) => s.id === l.status) && l.status && (
                        <option value={l.status}>{l.status}</option>
                      )}
                      {allStatuses.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select>
                  </td>
                  <td className="px-4 py-3 max-w-[220px]" data-testid={`lead-last-remark-${l.id}`}>
                    {lastRemark(l) ? (
                      <span
                        className="text-xs text-muted-foreground line-clamp-2 block"
                        title={lastRemark(l)}
                      >
                        {lastRemark(l)}
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground/50">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground tabular-nums text-xs">
                    {new Date(l.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(l)} data-testid={`edit-lead-${l.id}`} className="p-1.5 hover:bg-secondary border border-transparent hover:border-border">
                        <PencilSimple size={14} />
                      </button>
                      {isAdmin && (
                        <button onClick={() => remove(l)} data-testid={`delete-lead-${l.id}`} className="p-1.5 hover:bg-destructive/10 text-destructive border border-transparent hover:border-destructive/40">
                          <Trash size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {hasMore && (
          <div className="mt-3 flex items-center justify-center">
            <button
              onClick={loadMore}
              disabled={isLoadingMore}
              data-testid="leads-load-more"
              className="px-6 py-2.5 border-2 border-primary text-primary text-xs uppercase tracking-[0.15em] font-bold hover:bg-primary hover:text-primary-foreground disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoadingMore
                ? "Loading…"
                : `Load more · ${(total - leads.length).toLocaleString()} remaining`}
            </button>
          </div>
        )}
      </div>

      {/* Bulk reassign modal */}
      {bulkOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setBulkOpen(false)}>
          <div className="bg-white border border-border w-full max-w-md fade-up" onClick={(e) => e.stopPropagation()} data-testid="bulk-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bulk action</div>
                <h2 className="font-display text-2xl font-bold tracking-tight leading-none">
                  Reassign {selected.size} lead{selected.size !== 1 && "s"}
                </h2>
              </div>
              <button onClick={() => setBulkOpen(false)} className="p-1.5 hover:bg-secondary"><X size={16} /></button>
            </div>
            <form onSubmit={applyBulk} className="p-6 space-y-4">
              <p className="text-xs text-muted-foreground">
                Leave a field blank to keep it unchanged. At least one field must be set.
              </p>
              {isAdmin && (
                <Field label="New agent">
                  <select value={bulkForm.agent_id} onChange={(e) => setBulkForm({ ...bulkForm, agent_id: e.target.value })}
                    data-testid="bulk-agent-select"
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    <option value="">— Keep current —</option>
                    {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </Field>
              )}
              <Field label="New branch">
                <select value={bulkForm.branch_id} onChange={(e) => setBulkForm({ ...bulkForm, branch_id: e.target.value })}
                  data-testid="bulk-branch-select"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="">— Keep current —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </Field>
              <Field label="New status">
                <select value={bulkForm.status} onChange={(e) => setBulkForm({ ...bulkForm, status: e.target.value })}
                  data-testid="bulk-status-select"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                  <option value="">— Keep current —</option>
                  {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </Field>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={() => setBulkOpen(false)}
                  className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">Cancel</button>
                <button type="submit" data-testid="bulk-apply"
                  className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                  Apply to {selected.size}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk remark modal */}
      {bulkRemarkOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setBulkRemarkOpen(false)}>
          <div className="bg-white border border-border w-full max-w-md fade-up" onClick={(e) => e.stopPropagation()} data-testid="bulk-remark-modal">
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Bulk remark</div>
                <h2 className="font-display text-2xl font-bold tracking-tight leading-none">
                  Add remark to {selected.size} lead{selected.size !== 1 && "s"}
                </h2>
              </div>
              <button onClick={() => setBulkRemarkOpen(false)} className="p-1.5 hover:bg-secondary"><X size={16} /></button>
            </div>
            <form onSubmit={applyBulkRemark} className="p-6 space-y-4">
              <p className="text-xs text-muted-foreground">
                The same remark will be added to every selected lead&apos;s timeline.
              </p>
              <Field label="Remark *">
                <textarea
                  autoFocus required value={bulkRemark}
                  onChange={(e) => setBulkRemark(e.target.value)}
                  data-testid="bulk-remark-input"
                  rows={4}
                  placeholder="e.g. Followed up — interested in package, callback Friday."
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none"
                />
              </Field>
              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={() => setBulkRemarkOpen(false)}
                  className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">Cancel</button>
                <button type="submit" data-testid="bulk-remark-apply"
                  className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm">
                  Add to {selected.size}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal */}
      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={close}>
          <div
            className="bg-white border border-border w-full max-w-lg max-h-[90vh] overflow-y-auto fade-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="lead-modal"
          >
            <div className="flex items-center justify-between border-b border-border px-6 py-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  {editing === "new" ? "Create" : "Edit"}
                </div>
                <h2 className="font-display text-2xl font-bold tracking-tight leading-none">
                  {editing === "new" ? "New lead" : editing.customer_name}
                </h2>
              </div>
              <button onClick={close} className="p-1.5 hover:bg-secondary" data-testid="lead-modal-close">
                <X size={16} />
              </button>
            </div>

            <form onSubmit={save} className="p-6 space-y-4">
              <Field label="Customer name *">
                <input required value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                  data-testid="lead-form-name"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
              </Field>
              <Field label="Phone">
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  data-testid="lead-form-phone"
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
              </Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Source *">
                  <select required value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}
                    data-testid="lead-form-source"
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    {SOURCES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
                <Field label="Status">
                  <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}
                    data-testid="lead-form-status"
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    {STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Branch">
                  <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                    data-testid="lead-form-branch"
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    <option value="">— None —</option>
                    {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                  </select>
                </Field>
                <Field label="Agent *">
                  <select required value={form.agent_id} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
                    data-testid="lead-form-agent"
                    disabled={!isAdmin}
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none disabled:bg-secondary disabled:cursor-not-allowed">
                    <option value="">— Select —</option>
                    {(isAdmin ? agents : agents.filter((a) => a.id === user.id)).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Campaign">
                  <select value={form.campaign_id} onChange={(e) => setForm({ ...form, campaign_id: e.target.value })}
                    data-testid="lead-form-campaign"
                    className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none">
                    <option value="">— Not attributed —</option>
                    {campaigns.map((c) => <option key={c.id} value={c.id}>{c.name}{c.active === false ? " · paused" : ""}</option>)}
                  </select>
                </Field>
                {["converted", "booked", "existing_client"].includes(form.status) && (
                  <Field label="Revenue (₹)">
                    <input type="number" step="1" min="0"
                      value={form.revenue}
                      onChange={(e) => setForm({ ...form, revenue: e.target.value })}
                      placeholder="e.g. 25000"
                      data-testid="lead-form-revenue"
                      className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
                  </Field>
                )}
              </div>
              <Field label="Notes">
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  data-testid="lead-form-notes"
                  rows={3}
                  className="w-full px-3 py-2 border border-border rounded-sm bg-white text-sm focus:border-primary focus:outline-none" />
              </Field>

              {editing !== "new" && editing?.status_history?.length > 0 && (
                <div className="border-t border-border pt-4">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
                    Status history
                  </div>
                  <ul className="space-y-1.5 text-xs">
                    {[...editing.status_history].reverse().map((h, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <StatusBadge status={h.status} />
                        <span className="text-muted-foreground tabular-nums">{new Date(h.at).toLocaleString()}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2 border-t border-border">
                <button type="button" onClick={close}
                  className="px-4 py-2 text-sm font-semibold border border-border hover:bg-secondary rounded-sm">
                  Cancel
                </button>
                <button type="submit" disabled={saving} data-testid="lead-form-submit"
                  className="px-4 py-2 text-sm font-semibold bg-primary text-primary-foreground hover:bg-[#002277] rounded-sm disabled:opacity-60">
                  {saving ? "Saving…" : editing === "new" ? "Create lead" : "Save changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ImportLeadsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onDone={load}
        branches={branches}
        agents={agents}
      />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-foreground/70 block mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
